import {
  type EventPayload,
  type RunRecord,
  applyEvent,
  initialState,
  serializeRun,
} from '@orgsim/shared';
import { useState } from 'react';
import { downloadText } from '../download';
import { describeEvent, formatTime } from '../labels';

type Props = { run: RunRecord; readOnly: boolean; dispatch: (p: EventPayload) => void };

export function ProtocolPanel({ run, readOnly, dispatch }: Props) {
  const [note, setNote] = useState('');
  const injectTitle = (id: string) => run.scenario.injects.find((i) => i.id === id)?.title ?? id;

  // Describe each event against the state *before* it, so removed signs still have a label.
  const rows: { id: string; time: number; text: string }[] = [];
  let s = initialState(run.scenario);
  for (const e of run.events) {
    rows.push({ id: e.id, time: e.time, text: describeEvent(e, s, injectTitle) });
    s = applyEvent(s, e);
  }

  const exportProtocol = () => {
    const text = rows.map((r) => `${formatTime(r.time)}  ${r.text}`).join('\n');
    downloadText(
      `${run.scenario.name}-protokoll.txt`,
      `${run.scenario.name}, ${new Date(run.startedAt).toLocaleString('de-DE')}\n\n${text}\n`,
    );
  };

  return (
    <div className="space-y-3">
      {!readOnly ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) dispatch({ type: 'NoteAdded', text: note.trim() });
            setNote('');
          }}
          className="flex gap-1"
        >
          <input
            aria-label="Protokollnotiz"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notiz"
            className="flex-1 rounded bg-neutral-800 px-2 py-1"
          />
          <button type="submit" className="rounded bg-neutral-800 px-2 py-1">
            Eintragen
          </button>
        </form>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={exportProtocol}
          className="rounded bg-neutral-800 px-2 py-1 text-xs"
        >
          Protokoll als Text
        </button>
        <button
          type="button"
          onClick={() =>
            downloadText(`${run.scenario.name}-durchfuehrung.orgsim.json`, serializeRun(run))
          }
          className="rounded bg-neutral-800 px-2 py-1 text-xs"
        >
          Durchführung als JSON
        </button>
      </div>
      <ol className="space-y-1 font-mono text-xs">
        {rows.map((r) => (
          <li key={r.id} className="flex gap-2">
            <span className="text-neutral-500">{formatTime(r.time)}</span>
            <span>{r.text}</span>
          </li>
        ))}
        {rows.length === 0 ? <li className="text-neutral-500">Noch keine Ereignisse.</li> : null}
      </ol>
    </div>
  );
}
